import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { ApiError } from "../api/client";
import { useSignup } from "../api/auth";
import { AuthShell } from "../components/AuthShell";

/**
 * Bootstrap-only signup. The server returns 403 once any profile
 * exists; in that case we direct the user to /login.
 */
export function SignupPage() {
  const [email, setEmail] = useState("");
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const signup = useSignup();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    try {
      await signup.mutateAsync({ email, nom, prenom, password });
      navigate("/");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setError(
            "L'inscription publique est fermée. Demandez une invitation à un administrateur.",
          );
        } else if (err.status === 400) {
          setError("Domaine email non autorisé.");
        } else {
          setError("Erreur d'inscription. Réessayez.");
        }
      }
    }
  };

  return (
    <AuthShell
      title="Créer le premier compte"
      subtitle="Le premier compte créé devient l'administrateur de l'organisation."
      footer={
        <Link
          to="/login"
          className="text-ink dark:text-ink-dark underline underline-offset-2 hover:text-ink/70 dark:hover:text-ink-dark/70"
        >
          Déjà un compte ?
        </Link>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted dark:text-muted-dark block mb-1" htmlFor="prenom">
              Prénom
            </label>
            <input
              id="prenom"
              type="text"
              required
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
            />
          </div>
          <div>
            <label className="text-xs text-muted dark:text-muted-dark block mb-1" htmlFor="nom">
              Nom
            </label>
            <input
              id="nom"
              type="text"
              required
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted dark:text-muted-dark block mb-1" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
          />
        </div>
        <div>
          <label className="text-xs text-muted dark:text-muted-dark block mb-1" htmlFor="password">
            Mot de passe
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
          />
          {/* Inline strength indicator. Replaces the static "(8 minimum)"
              label so the user gets feedback as they type. */}
          <p
            className={`text-xs mt-1 ${
              password.length === 0
                ? "text-muted dark:text-muted-dark"
                : password.length < 8
                ? "text-chip-red"
                : "text-chip-green"
            }`}
          >
            {password.length === 0
              ? "8 caractères minimum"
              : password.length < 8
              ? `Encore ${8 - password.length} caractère${8 - password.length > 1 ? "s" : ""}`
              : "✓ Longueur suffisante"}
          </p>
        </div>
        {error && <div className="text-sm text-chip-red" role="alert">{error}</div>}
        <button
          type="submit"
          disabled={signup.isPending}
          className="w-full px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-ink/90 disabled:opacity-50"
        >
          {signup.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Créer le compte
        </button>
      </form>
    </AuthShell>
  );
}
